import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ChevronLeft, ChevronRight, Clock } from 'lucide-react';

export default function AdminCalendar() {
  const [bookings, setBookings] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(true);

  const dateStr = currentDate.toISOString().split('T')[0];

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/bookings?date=${dateStr}`).then(r => { setBookings(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [dateStr]);

  const prevDay = () => setCurrentDate(d => { const n = new Date(d); n.setDate(n.getDate() - 1); return n; });
  const nextDay = () => setCurrentDate(d => { const n = new Date(d); n.setDate(n.getDate() + 1); return n; });
  const today = () => setCurrentDate(new Date());

  const hours = Array.from({ length: 10 }, (_, i) => i + 9); // 9am-6pm

  const statusColor = (s) => {
    const map = {
      pending: '#E8B931', confirmed: '#4A90E2', in_progress: '#E07A5F', completed: '#4A7C59', cancelled: '#D9534F',
    };
    return map[s] || '#888';
  };

  return (
    <div className="space-y-6" data-testid="admin-calendar-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1A1A1A]" style={{ fontFamily: 'Manrope' }}>Calendar</h1>
          <p className="text-sm text-[#5A5A5A] mt-1">Daily schedule view</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="rounded-lg" onClick={prevDay} data-testid="cal-prev-btn"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" size="sm" className="rounded-lg text-sm font-medium px-4" onClick={today} data-testid="cal-today-btn">Today</Button>
          <Button variant="outline" size="sm" className="rounded-lg" onClick={nextDay} data-testid="cal-next-btn"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      <div className="text-center">
        <h2 className="text-xl font-semibold text-[#2D2D2D]">
          {currentDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </h2>
        <p className="text-sm text-[#888] mt-1">{bookings.length} appointment{bookings.length !== 1 ? 's' : ''}</p>
      </div>

      <Card className="rounded-2xl border-[#E5E5E0] bg-white overflow-hidden" style={{ boxShadow: 'var(--shadow-soft)' }}>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#2D2D2D] border-t-transparent" /></div>
          ) : (
            <div className="divide-y divide-[#E5E5E0]">
              {hours.map(hour => {
                const hourStr = `${hour.toString().padStart(2, '0')}:`;
                const hourBookings = bookings.filter(b => b.time_slot?.startsWith(hourStr));
                return (
                  <div key={hour} className="flex min-h-[64px]" data-testid={`cal-hour-${hour}`}>
                    <div className="w-20 flex-shrink-0 px-3 py-3 bg-[#F9F9F7] text-xs font-medium text-[#888] flex items-start">
                      {hour > 12 ? `${hour - 12}:00 PM` : hour === 12 ? '12:00 PM' : `${hour}:00 AM`}
                    </div>
                    <div className="flex-1 p-2 space-y-1">
                      {hourBookings.map(b => (
                        <div key={b.appointment_id} className="flex items-center gap-3 rounded-xl px-3 py-2" style={{ background: `${statusColor(b.status)}15`, borderLeft: `3px solid ${statusColor(b.status)}` }} data-testid={`cal-booking-${b.appointment_id}`}>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-[#2D2D2D] truncate">{b.pet_name} - {b.client_name}</p>
                            <p className="text-xs text-[#5A5A5A]">{b.service_names?.join(', ')}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-xs font-medium text-[#2D2D2D]">{b.time_slot} - {b.end_time}</p>
                            <p className="text-xs text-[#888]">{b.staff_name}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
